import base64 from 'base-64';
import CryptoJS from 'crypto-js';
import {DataType} from 'models/board/itemData';
import Mustache from 'mustache';
import utf8 from 'utf8';
import {v4 as uuidv4} from 'uuid';

export const GenerateUuid = (): string => {
    return uuidv4();
};

export function toCamelCase(obj: any): any {
    if (!obj || typeof obj !== 'object') {
        return obj; // Return non-object values as is
    }

    if (Array.isArray(obj)) {
        return obj.map(toCamelCase); // Handle arrays recursively
    }

    return Object.keys(obj).reduce((result: {[key: string]: any}, key) => {
        const camelKey = key.charAt(0).toLowerCase() + key.slice(1); // Convert PascalCase to camelCase
        result[camelKey] = toCamelCase(obj[key]); // Recursively handle nested objects
        return result;
    }, {});
}

export function toPascalCase(obj: any): any {
    if (!obj || typeof obj !== 'object') {
        return obj; // Return non-object values as is
    }

    if (Array.isArray(obj)) {
        return obj.map(toPascalCase); // Handle arrays recursively
    }

    return Object.keys(obj).reduce((result: {[key: string]: any}, key) => {
        const pascalKey = key.charAt(0).toUpperCase() + key.slice(1); // Convert camelCase to PascalCase
        result[pascalKey] = toPascalCase(obj[key]); // Recursively handle nested objects
        return result;
    }, {});
}

export interface ItemSummaryVariablesType {
    [key: string]: any;
}
export function parsedDateValue(value: string) {
    if (value && value !== '') {
        const date = new Date(value);
        return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear().toString().slice(-2)}`;
    }
    return value;
}

export function ConvertBoardViewSummaryToValues(
    itemSummaryVariables: ItemSummaryVariablesType,
    summary: string,
): string {
    var output = Mustache.render(summary, itemSummaryVariables);
    return output;
}

export function hasTextContent(html: string): boolean {
    const strippedContent = html.replace(/<[^>]*>/g, '').trim(); // Remove all HTML tags
    return strippedContent.length > 0; // Check if any text remains
}

export function extractFirstElementText(html: string): string {
    // If no HTML tags, return trimmed text directly
    const hasHTML = /<[^>]+>/.test(html);
    if (!hasHTML) return html.trim();

    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Find the first element child deeply
    const firstElement = temp.firstElementChild;

    return firstElement?.textContent?.trim() || '';
}

export function extractTextFromHtml(html: string): string {
    // If no HTML tags, return trimmed text directly
    const hasHTML = /<[^>]+>/.test(html);
    if (!hasHTML) return html.trim();

    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent?.trim() || '';
}

export function safeParseJSON<T>(jsonString: string, fallback: T): T {
    try {
        const parsed = JSON.parse(jsonString);
        if (parsed && typeof parsed === 'object') {
            return parsed;
        }
    } catch (error) {
        console.error('JSON parse error:', jsonString, error);
    }
    return fallback;
}

export function decodeBase64<T>(value: string, withoutParse?: boolean): T {
    const bytes = base64.decode(value);
    if (withoutParse) {
        return utf8.decode(bytes) as T;
    }
    const text: T = JSON.parse(utf8.decode(bytes)) as T;
    return text;
}
export function encodeBase64(value: string): string {
    return base64.encode(value);
}
export function encrypt(text: string, secretKey: string): string {
    return CryptoJS.AES.encrypt(text, secretKey).toString();
}

export function decrypt(encryptedText: string, secretKey: string): string {
    try {
        const bytes = CryptoJS.AES.decrypt(encryptedText, secretKey);
        return bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
        console.error('Decryption failed:', error);
        return '';
    }
}
//take milliseconds and return a seconds or minute of hours based on the value
export function formatDuration(milliseconds: number): string {
    if (milliseconds < 1000) {
        return `${milliseconds} ms`;
    } else if (milliseconds < 60000) {
        const seconds = Math.floor(milliseconds / 1000);
        return `${seconds} sec${seconds !== 1 ? 's' : ''}`;
    } else if (milliseconds < 3600000) {
        const minutes = Math.floor(milliseconds / 60000);
        return `${minutes} min${minutes !== 1 ? 's' : ''}`;
    } else {
        const hours = Math.floor(milliseconds / 3600000);
        return `${hours} hr${hours !== 1 ? 's' : ''}`;
    }
}
